import { FC } from "react";

import Stack from "@mui/material/Stack";
import {
  AvailablePaymentCurrency,
  formatCurrencyAmount,
  formatDateToMedDate,
  getDollarValue,
  LAST_DAY_OF_MONTH,
  SubscriptionRecurrenceInterval,
} from "@simplyk/common";
import { Info } from "luxon";

import { DayGrid } from "./DayGrid/DayGrid";
import { ChargeDateChange, ChargeDayOfMonth, formatMonthName, useEditChargeDate } from "./helpers/useEditChargeDate";
import { MonthNavigator } from "./MonthNavigator/MonthNavigator";
import { WeekdayList } from "./WeekdayList/WeekdayList";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { formatDayOfMonthOrdinal } from "@/common/helpers/formatDayOfMonthOrdinal";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Autorenew, Date as DateIcon } from "@/icons/outlined";

const INTERVAL_KEYS = {
  [SubscriptionRecurrenceInterval.Weekly]: "weekly",
  [SubscriptionRecurrenceInterval.BiWeekly]: "biWeekly",
  [SubscriptionRecurrenceInterval.Monthly]: "monthly",
  [SubscriptionRecurrenceInterval.Quarterly]: "quarterly",
  [SubscriptionRecurrenceInterval.Yearly]: "yearly",
} as const;

export interface EditChargeDateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newChargeDate: ChargeDateChange) => Promise<boolean | void>;
  isLoading: boolean;
  variant: "donor" | "organization";
  nextChargeDate: Date | string;
  recurrenceInterval: SubscriptionRecurrenceInterval;
  chargeAmount?: number;
  currency: AvailablePaymentCurrency;
  timezone?: string | null;
  inline?: boolean;
}

export const EditChargeDateDialog: FC<EditChargeDateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  variant,
  nextChargeDate,
  recurrenceInterval,
  chargeAmount,
  currency,
  timezone,
  inline,
}) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();

  const {
    isYearly,
    isMonthly,
    currentDay,
    isWeekBased,
    initialDay,
    currentMonth,
    currentWeekday,
    selectedDay,
    selectedMonth,
    selectedWeekday,
    viewedMonth,
    hasChanged,
    chargeDateChange,
    previewDate,
    isProrated,
    proratedAmount,
    selectDay,
    selectWeekday,
    goToPreviousMonth,
    goToNextMonth,
    reset,
  } = useEditChargeDate({ nextChargeDate, recurrenceInterval, chargeAmount, timezone });

  const intervalKey = INTERVAL_KEYS[recurrenceInterval];
  const weekdayName = (weekday: number) => Info.weekdays("long", { locale: isoLocale })[weekday - 1];
  const dayLabel = (day: ChargeDayOfMonth | null) =>
    day === LAST_DAY_OF_MONTH
      ? t("dashboard", "editChargeDateDialog.lastDay")
      : formatDayOfMonthOrdinal(day ?? currentDay, isoLocale);

  const formatAmount = (amount: number) =>
    formatCurrencyAmount({ value: getDollarValue(amount), locale: isoLocale, currency });
  const previewValues = {
    date: formatDateToMedDate(previewDate, isoLocale, timezone || "UTC"),
    day: dayLabel(selectedDay),
    weekday: weekdayName(selectedWeekday),
  };

  const getPreview = () => {
    if (!isProrated) {
      return t("dashboard", `editChargeDateDialog.preview.${intervalKey}`, previewValues);
    }
    if (proratedAmount === null || chargeAmount === undefined) {
      return t("dashboard", `editChargeDateDialog.proratedPreviewWithoutAmount.${intervalKey}`, previewValues);
    }
    return t("dashboard", `editChargeDateDialog.proratedPreview.${intervalKey}`, {
      ...previewValues,
      proratedAmount: formatAmount(proratedAmount),
      amount: formatAmount(chargeAmount),
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (!chargeDateChange) {
      return;
    }
    const success = await onConfirm(chargeDateChange);
    if (success !== false) {
      handleClose();
    }
  };

  return (
    <DashboardDialog open={open} onClose={handleClose} maxWidth="xs" vibe="brand" inline={inline}>
      <DashboardDialogBody
        slots={{
          header: (
            <DashboardDialogHeader
              icon={<Autorenew />}
              title={t("dashboard", `editChargeDateDialog.title.${intervalKey}`)}
            />
          ),
          content: (
            <DashboardDialogContent>
              <Stack gap={1.5}>
                <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                  {t("dashboard", `editChargeDateDialog.description.${variant}.${intervalKey}`, {
                    day: dayLabel(initialDay),
                    month: formatMonthName(currentMonth, isoLocale),
                    weekday: weekdayName(currentWeekday),
                  })}
                </DashboardTypography>

                {isWeekBased ? (
                  <WeekdayList key={currentWeekday} selectedWeekday={selectedWeekday} onSelectWeekday={selectWeekday} />
                ) : (
                  <Stack>
                    {isYearly && (
                      <MonthNavigator
                        monthLabel={formatMonthName(viewedMonth, isoLocale)}
                        onPrevious={goToPreviousMonth}
                        onNext={goToNextMonth}
                      />
                    )}
                    <DayGrid
                      selectedDay={!isYearly || viewedMonth === selectedMonth ? selectedDay : null}
                      currentDay={!isYearly || viewedMonth === currentMonth ? initialDay : null}
                      onSelectDay={selectDay}
                      showLastDay={isMonthly}
                    />
                  </Stack>
                )}

                {hasChanged && (
                  <DashboardInfobox vibe="brand" size="small" icon={<DateIcon />}>
                    {getPreview()}
                  </DashboardInfobox>
                )}
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton onClick={handleConfirm} isLoading={isLoading} disabled={!hasChanged}>
                {t("dashboard", "editChargeDateDialog.save")}
              </DashboardButton>
              <DashboardButton onClick={handleClose}>{t("dashboard", "editChargeDateDialog.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
